/**
 * TaskEventSyncManager - Writes task-created events to the device calendar
 * so they appear in native calendar apps and FimFam calendar views.
 */

import { deviceCalendarService } from './DeviceCalendarService';
import type { CreateCalendarEventInput } from '@/types/events';
import type { Subtask } from '@/types/tasks';

type TaskEventData = {
  title: string;
  startDate: string;
  startTime?: string | null;
  endDate?: string | null;
  endTime?: string | null;
  allDay?: boolean;
  description?: string | null;
  location?: string | null;
  subtasks?: Subtask[];
};

export class TaskEventSyncManager {
  /**
   * Create a device calendar event from task event data.
   * Returns the device event ID, or null if creation fails.
   */
  async createDeviceEvent(
    eventData: TaskEventData,
    calendarId: string,
  ): Promise<string | null> {
    try {
      const subtasksText = this.formatSubtasksForDescription(eventData.subtasks);
      const fullDescription = eventData.description
        ? `${eventData.description}${subtasksText}`
        : subtasksText || undefined;

      const input: CreateCalendarEventInput = {
        title: eventData.title,
        startDate: eventData.startDate,
        startTime: eventData.startTime ?? undefined,
        endDate: eventData.endDate ?? eventData.startDate,
        endTime: eventData.endTime ?? undefined,
        allDay: eventData.allDay ?? false,
        description: fullDescription,
        location: eventData.location ?? undefined,
      };

      const createdEvent = await deviceCalendarService.createEvent(calendarId, input);
      console.log('[TaskEventSyncManager] Device event created:', createdEvent.id);
      return createdEvent.id;
    } catch (error) {
      console.error('[TaskEventSyncManager] Failed to create device event:', error);
      return null;
    }
  }

  /**
   * Format subtasks as a checklist for inclusion in event description.
   */
  private formatSubtasksForDescription(subtasks?: Subtask[]): string {
    if (!subtasks?.length) return '';
    const lines = subtasks.map(s => `${s.completed ? '[x]' : '[ ]'} ${s.title}`);
    return '\n\nSubtasks:\n' + lines.join('\n');
  }
}

export const taskEventSyncManager = new TaskEventSyncManager();
